import { useEffect, useRef, useState } from 'react';
import { useGlobalStore } from '../../stores/global-store';
import { useView } from '../view/use-view';
import { useToastStore } from '../../stores/toast-store';
import { navigate } from '../../app/navigation';
import { environment } from '../../config/environment';
import type { CuratedPlaylist } from '../../interfaces/curated-playlist';
import AppImage from '../../shared/components/AppImage';
import SpinnerLoading from '../../shared/components/SpinnerLoading';

// Port of src/app/components/top-playlists/ (mypart-top-playlists).
// The Angular .scss file was empty — no stylesheet.
//
// The single full-screen curated overlay. Rendered once by App when the derived
// view is 'curated' (the /top-playlists and /playlist/:artist URLs); the modal is
// shown/hidden via the `modalVisible` state, toggled by the view effect below.
//
// NOTE(port): Angular used the Flowbite Modal JS component (backdrop 'dynamic',
// closable: false, placement 'top-center', backdropClasses 'empty') to toggle
// the always-rendered overlay div between hidden/shown. The React port keeps the
// same overlay markup permanently rendered and toggles the 'hidden' class — same
// observable behavior (non-closable full-screen overlay) without the Flowbite JS
// dependency.

function generateStructuredDataForCollection(playlists: CuratedPlaylist[]): void {
  const itemListElement = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': playlists.map((playlist, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'MusicPlaylist',
        'name': playlist.name,
        'description': playlist.description,
        'image': playlist.imageUrl,
        'url': playlist.songhuntLink || playlist.spotifyLink
      }
    }))
  };

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify(itemListElement);
  document.head.appendChild(script);
}

export default function TopPlaylists() {
  const view = useView();
  const curatedPlaylists = useGlobalStore(s => s.curatedPlaylists);
  const appTheme = useGlobalStore(s => s.appTheme);

  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const curatedPlaylistsPageRef = useRef<HTMLDivElement>(null);

  // Port of the ngAfterViewInit effect() on showCuratedPlaylists.
  useEffect(() => {
    if (view !== 'curated') {
      return;
    }
    const globalStore = useGlobalStore.getState();

    if (globalStore.errorRetrievingPlaylists) {
      useToastStore.getState().warning(`Could not retrieve playlists. ${environment.toastSupportMessage}`);
    } else {
      setIsLoading(true);

      const currentPlaylists = globalStore.curatedPlaylists;
      if (currentPlaylists) {
        generateStructuredDataForCollection(currentPlaylists);
      }

      // showModal()
      setModalVisible(true);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const hideModal = () => {
    setModalVisible(false);
    // Navigate home via the router so useView leaves 'curated' and this overlay
    // unmounts (the old code pushState'd the trimmed URL + flipped the flag).
    navigate('/');
  };

  return (
    <div ref={curatedPlaylistsPageRef}
         className={`z-40 fixed top-0 left-0 right-0 bottom-0 p-4 overflow-x-hidden overflow-y-hidden md:inset-0
      h-full w-full light-gradient dark:dark-gradient ${modalVisible ? '' : 'hidden'}`}
         id="curated-playlist-page"
         tabIndex={-1}>
      <div className="relative w-full h-full">
        {isLoading && <SpinnerLoading/>}
        <button
            onClick={hideModal}
            className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900
       rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
            type="button">
          <svg aria-hidden="true" className="w-3 h-3" fill="none" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
            <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"/>
          </svg>
          <span className="sr-only">Close modal</span>
        </button>

        <div className="w-full flex flex-wrap items-center justify-center sm:flex-row">
          {/* Title */}
          <div className="w-full order-last sm:order-none sm:w-auto text-center mt-4 sm:mt-0">
            <h1 className="text-xl leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              Top Playlists on Songhunt
            </h1>
            <h2 className="text-base tracking-normal text-gray-900 md:text-lg dark:text-white">
              Check out our favorite AI-curated playlists
            </h2>
          </div>
        </div>

        <div className="md:p-4 my-4 h-[90%] w-full flex justify-center">
          <div className="w-full grid gap-6 sm:grid-cols-fit-playlists overflow-y-auto">
            {curatedPlaylists?.map((playlist) => (
              /* Main playlist card container */
              <div key={playlist.id}
                   className="w-full flex sm:flex-col items-start sm:items-center space-x-4 sm:space-x-0 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">

                {/* Left Side (small screens) / Top (large screens) - Playlist Image */}
                <div className="flex-shrink-0">
                  <a href={playlist.songhuntLink}
                     className="w-[96px] h-[96px] sm:w-full sm:h-auto object-cover rounded-lg"
                     title={`Explore the '${playlist.name}' playlist on Songhunt`}>
                    <AppImage alt={`Cover image for ${playlist.name} playlist`} src={playlist.imageUrl}
                              className="w-[96px] h-[96px] sm:w-full sm:h-auto object-cover rounded-lg"
                              loading="lazy"/>
                  </a>
                </div>

                {/* Right Side (small screens) / Bottom (large screens) - Playlist Info */}
                <div className="flex-1 min-w-0 w-full sm:mt-4">
                  {/* Playlist Name */}
                  <p className="text-sm md:text-base font-bold text-gray-900 dark:text-white">
                    {playlist.name}
                  </p>

                  {/* Links - Songhunt and Spotify */}
                  <div className="flex space-x-4 mt-2">
                    <span className="text-gray-900 dark:text-white text-sm">Listen On:</span>
                    <a href={playlist.songhuntLink}
                       className="w-[74px] h-[24px] relative items-center logo-container"
                       title={`Explore the '${playlist.name}' playlist on Songhunt`}>
                      {appTheme === 'dark' ? (
                        <AppImage alt="Songhunt Dark Logo" className="absolute inset-0 w-full h-full" loading="lazy"
                                  src="https://d382nvfdu38z2f.cloudfront.net/songhunt/logo_dark.webp"/>
                      ) : (
                        <AppImage alt="Songhunt Light Logo" className="absolute inset-0 w-full h-full" loading="lazy"
                                  src="https://d382nvfdu38z2f.cloudfront.net/songhunt/logo_light.webp"/>
                      )}
                    </a>

                    <a href={playlist.spotifyLink}
                       className="w-[74px] h-[24px] relative items-center logo-container"
                       target="_blank" rel="noreferrer" title={`Explore the '${playlist.name}' playlist on Spotify`}>
                      <AppImage alt="Spotify Logo" className="absolute inset-0 w-full h-full" loading="lazy"
                                src="assets/spotify.png"/>
                    </a>
                  </div>

                  {/* Playlist Description */}
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {playlist.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
